import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import useDevice from './../../../../hooks/common/useDevice';
import NavigationBodyContents from './NavigationBodyContents';
import NavigationBodyMobileContents from './NavigationBodyMobileContents';
import Video from './../video/Video';
import Portal from '../../Portal';
import Calculator from '../calculator/Calculator';
import { POPUP_INDEX } from '../../../../styles/constants/priority';
import ModalBase from '../../modal/ModalBase';
import useSwitching from '../../../../hooks/common/useSwitching';

interface Props {
  closeEvent: () => void;
}

/**
 *  네비게이션 내용 PC버전 컴포넌트
 * @param {function} closeEvent 네비게이션 비활성화 클릭 함수
 *  */
function NavigationBody({ closeEvent }: Props): JSX.Element {
  const { pathname } = useRouter();
  const device: string = useDevice();

  const [groupNameList, setGroupNameList] = useState<GroupName[]>(GROUP_NAME_LIST);
  const [itemList, setItemList] = useState<Item[]>(ITEM_LIST);
  const [activeVideo, switchingActiveVideo]: [boolean, () => void] = useSwitching();
  const [activeCalculator, switchingActiveCaculator]: [boolean, () => void] = useSwitching();

  /**
   *  대제목 클릭 이벤트
   * @param {string} name 대제목 이름
   *  */
  const clickGroupNameEvent = (name: string): void => {
    setGroupNameList(prev =>
      prev.map(
        group =>
          group && {
            ...group,
            active: group.name === name,
          }
      )
    );
  };

  useEffect(() => {
    /* 페이지 이동에 따른 헤더의 대제목 활성화 설정 */
    const nowGroup: string = ITEM_LIST.find(item => item.href === pathname)?.group || '';

    setGroupNameList(prev =>
      prev.map(
        (groupName: GroupName) =>
          groupName && {
            ...groupName,
            active: groupName.name === nowGroup,
          }
      )
    );

    setItemList(prev =>
      prev.map(
        (item: Item) =>
          item && {
            ...item,
            active: item.href === pathname,
          }
      )
    );
  }, [pathname]);

  return (
    <>
      {device === 'pc' ? (
        <NavigationBodyContents
          groupNameList={groupNameList}
          itemList={itemList}
          openVideo={switchingActiveVideo}
          openCalculator={switchingActiveCaculator}
          closeEvent={closeEvent}
        />
      ) : (
        <NavigationBodyMobileContents
          groupNameList={groupNameList}
          itemList={itemList}
          clickGroupNameEvent={clickGroupNameEvent}
          openVideo={switchingActiveVideo}
          openCalculator={switchingActiveCaculator}
          closeEvent={closeEvent}
        />
      )}

      {activeVideo && (
        <Portal>
          <Video siwtchVideo={switchingActiveVideo} />
        </Portal>
      )}

      <ModalBase
        type='modal'
        active={activeCalculator}
        zIndex={POPUP_INDEX}
        closeEvent={switchingActiveCaculator}
      >
        <Calculator />
      </ModalBase>
    </>
  );
}

interface GroupName {
  name: string;
  active: boolean;
}

interface Item {
  name: string;
  group: string;
  href?: string;
  action?: string;
  active: boolean;
}

const GROUP_NAME_LIST: GroupName[] = [
  { name: '신차견적', active: false },
  { name: '핫딜', active: false },
  { name: '자동차 정보', active: false },
  { name: '마이페이지', active: false },
];

const ITEM_LIST: Item[] = [
  { name: '신차견적', group: GROUP_NAME_LIST[0].name, href: '/estimate/new', active: false },
  {
    name: '리스/렌트',
    group: GROUP_NAME_LIST[0].name,
    href: 'https://solecar.co.kr/estimate/leaseRent',
    active: false,
  },
  {
    name: '중고차',
    group: GROUP_NAME_LIST[1].name,
    href: '/view/estimate/usedIntro',
    active: false,
  },
  { name: '수입차', group: GROUP_NAME_LIST[1].name, href: '/estimate/import', active: false },
  {
    name: '제원비교',
    group: GROUP_NAME_LIST[2].name,
    href: '/specification_comparison',
    active: false,
  },
  { name: '가격표', group: GROUP_NAME_LIST[2].name, href: '/car/price/list/page', active: false },
  { name: '카달로그', group: GROUP_NAME_LIST[2].name, href: '/estimate/catalog', active: false },
  {
    name: '할부조건표',
    group: GROUP_NAME_LIST[2].name,
    href: '/new_car_purchase_condition_table',
    active: false,
  },
  { name: '판매조건표', group: GROUP_NAME_LIST[2].name, href: '/sales_condition', active: false },
  {
    name: '할부계산기',
    group: GROUP_NAME_LIST[2].name,
    action: 'calculator',
    active: false,
  },
  { name: '비디오', group: GROUP_NAME_LIST[2].name, action: 'video', active: false },
  { name: '마이페이지', group: GROUP_NAME_LIST[3].name, href: '/mypage', active: false },
  { name: '견적관리', group: GROUP_NAME_LIST[3].name, href: '/estimate_management', active: false },
  { name: '공지사항', group: GROUP_NAME_LIST[3].name, href: '/notice', active: false },
  { name: '1:1문의', group: GROUP_NAME_LIST[3].name, href: '/inquiry', active: false },
  {
    name: '이용안내',
    group: GROUP_NAME_LIST[3].name,
    href: '/common/use_guide.html',
    active: false,
  },
];

export default React.memo(NavigationBody);
